import React from 'react';
import { Sparkles, RefreshCw, Copy, Trash2, Loader2, Lock, Unlock, Users, Package, MapPin, X } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import Tooltip from '../ui/Tooltip';
import AutoTextarea from '../ui/AutoTextarea';
import ShotReferences from './ShotReferences';
import type { StoryboardShot, ShotCamera } from '../../services/studioService';

/**
 * THE SHOT INSPECTOR — a docked properties panel for whatever is selected on the board.
 *
 * REPLACES THE FLOATING TOOLBAR (owner 2026-08-30: *"I don't like the menu on top of the shot cards, it feels
 * in the way"*), and the research agrees with the instinct rather than merely permitting it. Figma shipped
 * exactly this move in UI3 — floating the panels over the canvas — and it became the most contested change in
 * that release: floating panels "blurred the lines between tools and the canvas, creating visual clutter that
 * actually hurt usability". Our bar was worse on the axis that matters: it rendered WIDER than the card it
 * acted on, directly over the canvas. Docked is where every mature canvas tool keeps this — Figma's right
 * sidebar holds layer properties, Adobe's Properties panel holds attributes with the generative actions beside
 * them. It lives INSIDE the centre pane, so the workstation is still three panes.
 *
 * ASK FIRST, TYPE SECOND, TOGGLE NEVER (owner 2026-08-30: *"move Actions to top, as not everyone knows what to
 * do with camera work… I still prefer to be prompt based"*, and *"the level of fine details in general is too
 * much for toggling"*).
 *
 * The first draft of this panel offered twenty chips — seven shot sizes, seven moves, three angles, three
 * pacings — which is a shot list wearing a UI. It fails the product on its own terms: PRODUCT.md sells
 * story-without-craft, and a creator who knew whether this beat wanted an MCU or an MS would not need us. Worse,
 * enumerated controls do not compose. "Push in slowly as she realises" is one thought; expressed as toggles it
 * becomes three lookups and still loses the "as she realises".
 *
 * So the order is deliberate: the crew is offered FIRST, then a plain sentence of direction, and the coverage
 * that results is REPORTED rather than operated. A creator who does know the craft can still write "low angle,
 * tight on her hands" and be understood exactly.
 */

export interface ShotInspectorProps {
  shot: StoryboardShot | null;
  /** 0-based; the panel titles itself with the 1-based number a creator sees on the card. */
  index: number | null;
  onChange?: (index: number, patch: Partial<StoryboardShot>) => void;
  onAssist?: (index: number) => void;
  onRemake?: (index: number) => void;
  onDuplicate?: (index: number) => void;
  onDelete?: (index: number) => void;
  /** Open the existing multi-select pickers. Kept as callbacks so the modals stay the board's business. */
  onPickPeople?: (index: number) => void;
  onPickObjects?: (index: number) => void;
  onPickPlace?: (index: number) => void;
  /** Name of the scene's location, when this shot's band has one. */
  placeName?: string | null;
  /** The episode this shot belongs to, so the panel can ask what its references will actually be (F3/F4). */
  storyboardId?: string | null;
  /** Bumped when the episode is written: a pick is only visible to the server once the save has landed. */
  savedTick?: number;
  /** How many shots the server holds. Omitted = unknown, and the strip asks as it always did. */
  savedShotCount?: number;
  assisting?: boolean;
  remaking?: boolean;
  /** True while ANY shot is mid-remake — a second charge must not be startable. */
  busy?: boolean;
  className?: string;
}

/** Plain words for the vocabulary the render stores. Read-only here: this REPORTS coverage, it does not set it. */
const SIZE_WORDS: Record<string, string> = {
  ECU: 'Extreme close', CU: 'Close', MCU: 'Chest up', MS: 'Waist up',
  MLS: 'Full body', WS: 'Wide', EWS: 'Very wide',
};
const MOVE_WORDS: Record<string, string> = {
  static: 'Locked off', push: 'Push in', pull: 'Pull out', pan: 'Pan',
  tilt: 'Tilt', orbit: 'Orbit', handheld: 'Handheld',
};
const PITCH_WORDS: Record<string, string> = { level: 'Eye level', high: 'From above', low: 'From below' };
const PACE_WORDS: Record<string, string> = { brief: 'Quick', normal: 'Normal', hold: 'Linger' };

/**
 * "Close · Push in · From below · rain on glass" — never the raw enum, which means nothing to the person
 * reading it. Sound is reported here rather than edited in a field of its own (owner 2026-08-30: it belongs
 * in the prompt): one sentence describes the shot, and this says what the sentence produced.
 */
function coverageWords(camera?: ShotCamera, weight?: string, sfx?: string): string[] {
  return [
    camera?.shot_size ? SIZE_WORDS[camera.shot_size] : '',
    camera?.move ? MOVE_WORDS[camera.move] : '',
    camera?.pitch ? PITCH_WORDS[camera.pitch] : '',
    weight && weight !== 'normal' ? PACE_WORDS[weight] : '',
    sfx || '',
  ].filter(Boolean);
}

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section className="border-t border-secondary-200 px-3 py-2.5 first:border-t-0 dark:border-secondary-800">
    <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>{label}</span>
    {children}
  </section>
);

const Action: React.FC<{
  label: string; hint: string; Icon: typeof Sparkles; onClick?: () => void; disabled?: boolean; busy?: boolean;
  testid: string; primary?: boolean;
}> = ({ label, hint, Icon, onClick, disabled, busy, testid, primary }) => (
  <Tooltip content={hint}>
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || !onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 ${TYPE.control} transition-colors
                 disabled:cursor-not-allowed disabled:opacity-40 ${
        primary
          ? 'border-secondary-900 bg-secondary-900 text-white hover:bg-secondary-800 dark:border-white dark:bg-white dark:text-secondary-900'
          : 'border-secondary-200 text-secondary-700 hover:border-secondary-400 dark:border-secondary-600 dark:text-secondary-200'
      }`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  </Tooltip>
);

const ShotInspector: React.FC<ShotInspectorProps> = ({
  shot, index, onChange, onAssist, onRemake, onDuplicate, onDelete,
  onPickPeople, onPickObjects, onPickPlace, placeName, storyboardId, savedTick, savedShotCount, assisting, remaking, busy, className = '',
}) => {
  /**
   * The panel keeps its WIDTH when nothing is selected rather than collapsing. A pane that appears and
   * disappears reflows the canvas under the creator's cursor on every click — the unpredictability that made
   * the floating bar feel wrong, reintroduced at a larger scale.
   */
  const empty = !shot || index == null;
  const coverage = empty ? [] : coverageWords(shot.camera, shot.weight, shot.sfx);

  return (
    <aside
      data-testid="shot-inspector"
      aria-label="Shot properties"
      className={`flex w-72 shrink-0 flex-col overflow-y-auto border-l border-secondary-200 bg-white
                 dark:border-secondary-800 dark:bg-secondary-950 ${className}`}
    >
      {empty ? (
        <p className={`px-3 py-3 ${TYPE.meta} leading-relaxed text-secondary-400 dark:text-secondary-500`}>
          Select a shot to direct it, or to ask your crew to.
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <span className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Shot {index + 1}</span>
            <Tooltip content={shot.locked ? 'Approved for spend' : 'Approve this shot for spend'}>
              <button
                type="button"
                data-testid="insp-lock"
                onClick={() => onChange?.(index, { locked: !shot.locked })}
                className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${TYPE.micro}
                           transition-colors ${
                  shot.locked
                    ? 'border-secondary-400 text-secondary-900 dark:border-secondary-500 dark:text-white'
                    : 'border-secondary-200 text-secondary-500 hover:border-secondary-400 dark:border-secondary-700'
                }`}
              >
                {shot.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                {shot.locked ? 'Approved' : 'Approve'}
              </button>
            </Tooltip>
          </div>

          {/* FIRST, because it is the answer for the creator who does not know what to do next — which is most
              of them, most of the time. Burying the crew under craft controls asks the question the product
              exists to remove. */}
          <Section label="Ask your crew">
            <div className="mt-1 flex flex-col gap-1">
              <Action
                primary
                testid="insp-assist"
                Icon={Sparkles}
                label="Ask a specialist"
                hint="Your crew sets this shot for you — framing, movement and pace"
                onClick={onAssist && index != null ? () => onAssist(index) : undefined}
                busy={assisting}
                disabled={assisting}
              />
              <Action
                testid="insp-remake"
                Icon={RefreshCw}
                label="Re-make this shot"
                hint="Generate this clip again (spends credits)"
                onClick={onRemake && index != null ? () => onRemake(index) : undefined}
                busy={remaking}
                disabled={busy}
              />
            </div>
          </Section>

          {/*
            THE REFERENCE MATERIAL, drawn from the PROJECT (owner 2026-08-30: *"user should be able to pick from
            Cast, Objects, Location for the reference materials which they set in Project-level"*).

            This is what makes a shot high quality rather than merely generated: naming the cast attaches their
            locked face and voice, naming an object pastes its wording byte-identically, and naming the place
            carries its anchor. Every one of them was authored once at project level, which is exactly why a
            character looks the same in episode 7 as in episode 2.

            All three are ALWAYS shown, and disabled when the project has none — rather than hidden, which
            would leave a creator with no way to learn that these come from the project at all. The hint says
            where to go.
          */}
          <Section label="In this shot">
            <div className="mt-1 flex flex-col gap-1">
              <Action
                testid="insp-people"
                Icon={Users}
                label={shot.character_ids?.length ? `Who (${shot.character_ids.length})` : 'Who is in it'}
                hint={onPickPeople ? 'Pick from your series cast — their locked face and voice come with them'
                  : 'Add people to your series cast first, then they can appear here'}
                onClick={onPickPeople && index != null ? () => onPickPeople(index) : undefined}
              />
              <Action
                testid="insp-objects"
                Icon={Package}
                label={shot.prop_ids?.length ? `Objects (${shot.prop_ids.length})` : 'Objects'}
                hint={onPickObjects ? 'Pick from your series objects — their exact wording is pasted in'
                  : 'Add objects to your series first, then they can appear here'}
                onClick={onPickObjects && index != null ? () => onPickObjects(index) : undefined}
              />
              <Action
                testid="insp-place"
                Icon={MapPin}
                label={placeName ? `Place: ${placeName}` : 'Where this happens'}
                hint={onPickPlace ? 'Pick from your series locations — the scene keeps its anchor'
                  : 'Add locations to your series first, then they can appear here'}
                onClick={onPickPlace && index != null ? () => onPickPlace(index) : undefined}
              />
            </div>
            {/*
              THE PICTURES THEMSELVES (F4, 2026-09-12). Counts are not a visual lock: a creator who approved a
              set plate had no way to see whether this shot would use it until the render came back. The strip
              runs the render's own resolution and shows what travels — and, just as usefully, what does not
              and why.
            */}
            <ShotReferences
              storyboardId={storyboardId}
              index={index}
              saved={savedShotCount == null || (index != null && index < savedShotCount)}
              signature={[
                (shot.character_ids || []).join(','),
                (shot.prop_ids || []).join(','),
                shot.location_id || '',
                // The save tick matters as much as the choice: a pick the server has not been told about yet
                // would otherwise be answered from stale state and never re-asked.
                String(savedTick ?? ''),
              ].join('|')}
            />
          </Section>

          {/*
            A SENTENCE, not a control panel. One thought — "push in slowly as she realises" — survives here and
            does not survive being decomposed into three enums.

            PLACED AFTER the material, and given real room (owner 2026-08-30). Both follow from how the work
            actually goes: a creator settles WHO and WHERE, then says what happens to them. Directing before
            choosing the cast is describing a shot you have not populated. And the field is where the shot is
            genuinely written, so a two-row box was quietly telling people to keep it short.
          */}
          <Section label="Direct this shot">
            <AutoTextarea
              value={shot.freeText || ''}
              onChange={(v) => onChange?.(index, { freeText: v })}
              minRows={5}
              placeholder="Say it plainly — tight on her face, push in slowly, rain on the glass."
              aria-label="Direction for this shot"
              data-testid="insp-direction"
              className={`mt-1 block w-full rounded-lg border border-secondary-200 bg-white px-2 py-1.5
                         ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                         placeholder:text-secondary-400 focus:border-secondary-400 dark:border-secondary-600
                         dark:bg-secondary-900 dark:text-white`}
            />
            {/* REPORTED, not operated. When the writer or a specialist has set coverage, this says what it is
                in plain words so the creator can see it and disagree — without having to learn the vocabulary
                to read it, or to operate twenty chips to change it. */}
            {coverage.length > 0 && (
              <div className="mt-1.5 flex items-start gap-1">
                <span
                  data-testid="insp-coverage"
                  className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}
                >
                  {coverage.join(' · ')}
                </span>
                <Tooltip content="Clear the camera and sound plan">
                  <button
                    type="button"
                    data-testid="insp-clear-coverage"
                    onClick={() => onChange?.(index, { camera: undefined, weight: undefined, sfx: undefined })}
                    aria-label="Clear the camera and sound plan"
                    className="shrink-0 rounded p-0.5 text-secondary-400 transition-colors hover:text-secondary-800 dark:hover:text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Tooltip>
              </div>
            )}
          </Section>

          {/* Structural verbs last, and away from the creative ones — a Delete a pixel from a Re-make is how
              work gets lost. */}
          <Section label="This shot">
            <div className="mt-1 flex gap-1">
              <Action
                testid="insp-duplicate"
                Icon={Copy}
                label="Duplicate"
                hint="Copy this shot"
                onClick={onDuplicate && index != null ? () => onDuplicate(index) : undefined}
              />
              <Action
                testid="insp-delete"
                Icon={Trash2}
                label="Delete"
                hint="Remove this shot"
                onClick={onDelete && index != null ? () => onDelete(index) : undefined}
              />
            </div>
          </Section>
        </>
      )}
    </aside>
  );
};

export default ShotInspector;
